/**
 * Shared INPUT sizing + field styling for Crew Forge.
 *
 * Why a new constants file rather than an existing one: `buttons.js` is class
 * strings BY BUTTON INTENT, `colors.js`/`statuses.js`/`taskStatuses.js` are
 * semantic value maps, `rowStyles.js` is table-row decoration, and
 * `budget.js`/`grading.js`/`vat.js`/`riskAssessment.js`/`raEmojiImages.js`/
 * `raReviewQuestions.js`/`gagBreakdown.js` are feature data. None of them is a
 * home for "how wide is a text input", and putting input sizing in `buttons.js`
 * would be a category error that the next person copies.
 *
 * Usage:
 *   import { CONTROL_HEIGHT, SEARCH_BOX_WIDTH, SEARCH_INPUT_FIELD } from '../constants/inputs';
 */

// ---------------------------------------------------------------------------
// Control height — ONE definition for every control on a sticky toolbar
// ---------------------------------------------------------------------------

/**
 * THE height of a control sitting on a `components/ui/Toolbar` band. Every
 * control on that bar must consume this, or the bar goes ragged.
 *
 * ── THE DEFECT THIS EXISTS TO CLOSE (measured, 2026-08-08) ──
 * The four toolbar pages carried FIVE different desktop heights between them,
 * because every control derived its height from its own padding + font size +
 * border width and no two agreed. All of them also carried the same
 * `min-h-[44px] md:min-h-0`, so they agreed on a phone by accident and went
 * ragged at exactly the width the product owner works at:
 *
 *   30px  Cards / Table            `py-1.5 text-xs`  + 1px wrapper border
 *   34px  search box               `py-1.5 text-sm`  + 1px border
 *   36px  All / Actionable         `py-1.5 text-sm`  + `border-2`
 *   36px  Sort ▾ / Filters ▾       `py-1.5 text-sm`  + `border-2`
 *   38px  Breakdown's Group ▾ / Filters ▾ / Views ▾ / Export / + New Gag
 *                                  `BTN.secondary` = `px-4 py-2 text-sm border`
 *   40px  Breakdown's Table | Timeline | Labour tabs
 *                                  32px segments inside a `p-1` track
 *
 * Product owner: *"all of this is not aligned, on the same rows etc. It's a
 * mess"*.
 *
 * 🔑 **DERIVING A HEIGHT FROM PADDING IS THE BUG, SO THE FIX DECLARES ONE.**
 * Tailwind's preflight sets `box-sizing: border-box` globally, so
 * `md:h-[2.375rem]` is the **OUTER** height whatever the border weight and
 * whatever the font size. That is the whole point: a control may keep its own
 * border or type size and still land on the same baseline as its neighbours.
 * Change a padding class on any of these controls now and the height does not
 * move.
 *
 * ── 🔺 WHY 38 — THE DIRECTION WAS A PRODUCT DECISION, NOT AN AVERAGE ──
 * Product owner, 2026-08-08: *"breakdown is probably the best looking menu
 * structure but I still want a unified structure across the site."* So the
 * conflict resolves **UPWARD, toward the Breakdown**, and the order pages come
 * up to meet it. Normalising down to 34 — the height more pages happened to use
 * — would have unified the site on the look he likes least.
 *
 * 38 is `BTN.secondary`'s natural height (`px-4 py-2 text-sm border` =
 * 20 + 16 + 2), i.e. the height of the CONTROL LANGUAGE the Breakdown's bar is
 * written in and which the order pages are adopting. Everything on every bar now
 * grows to it except the Breakdown's `Table | Timeline | Labour` track, which
 * comes DOWN 2px from 40.
 *
 * ⚠️ **40 was the other candidate and is a one-token edit if it is revisited**
 * (`md:h-[2.375rem]` → `md:h-10`). It is the only figure that would have left
 * that track bit-for-bit unchanged. It was rejected because the owner's follow-up
 * — *"still needs work to look professional tho"*, about the Breakdown — makes
 * that bar the **starting point rather than the target**: there is nothing on it
 * sacred enough to bend the shared number around, and 38 is the height of the
 * language rather than of one control.
 *
 * ⚠️ It is expressed in `rem`, not `px`, to match `SEARCH_BOX_WIDTH`'s
 * `sm:basis-[18rem]` below and the rest of Tailwind's scale — a px height stops
 * tracking the root font size while every padding around it keeps tracking it.
 *
 * 🔑 **`min-h-[44px]` BELOW `md` IS RETAINED AND IS NOT NEGOTIABLE.** It is the
 * mobile tap target every one of these controls already carried, and it is why
 * the bar looked *fine* on a phone and ragged on the desktop the owner works at:
 * below `md` all three controls were being floored to 44px by the same class, so
 * they agreed by accident. `md:h-9` only applies from `md` up, exactly where
 * `md:min-h-0` releases that floor — the two halves are one rule.
 *
 * ⚠️ A fixed height needs the label CENTRED IN IT. Give any control taking this
 * an `inline-flex items-center` (buttons) — a UA-default button centres its
 * label, but a `<div>` wrapper or a nested layout does not, and the failure is a
 * label sitting on the top edge of an otherwise correct box.
 *
 * ⚠️ **WHY HERE RATHER THAN `components/ui/Toolbar.js`**, which owns the bar and
 * already exports `TOOLBAR_DISCLOSURE_TRIGGER`: `SEARCH_INPUT_FIELD` below has
 * to consume it, and a file under `constants/` must not import from a file under
 * `components/`. Putting it in `Toolbar.js` inverts that dependency and points
 * `constants/` at the component tree. It goes the other way instead — `Toolbar.js`
 * imports this — which is also why this file is the one the brief named.
 *
 * ⚠️ Not genuinely testable under jsdom, for the same reason as everything else
 * in this file: no stylesheet, no layout, no computed height. A test can assert
 * that every bar control CONSUMES this constant (which is what
 * `constants/inputs.controlHeight.test.js` does, as a static scan) but not that
 * they render level. That is a browser check.
 */
export const CONTROL_HEIGHT = 'min-h-[44px] md:min-h-0 md:h-[2.375rem]';

// ---------------------------------------------------------------------------
// Search box width — ONE definition for every search box in the app
// ---------------------------------------------------------------------------

/**
 * The search box is 18rem (288px) and it is the SAME on every page. Product
 * owner's ruling, 2026-08-08: "same size on every page — fixed, may shrink,
 * must never grow", and mobile is full width with the fixed width applying
 * from `sm:` up (he chose that explicitly over "fixed on every screen").
 *
 * WHY 18rem, so nobody re-tunes it by feel:
 *  - It is sized to CONTENT. At the rendered `text-sm` (14px) it holds ~34
 *    characters, which fits "Search title, description, location…" — the
 *    longest placeholder that has to stay intact — plus a typical order
 *    reference with a supplier fragment after it.
 *  - It sits between the two fixed widths that were in the codebase when it was
 *    chosen (14rem at `TeamTaskBoard.js` `md:w-56`, 16rem at `PeopleManager.js`
 *    `w-64`), so adopting it reflows nothing badly. ⚠️ That second one is now
 *    HISTORY, not a description of the tree: `PeopleManager` adopted
 *    `components/ui/SearchInput` on 2026-08-10 and its `w-64` is gone, i.e. the
 *    reflow this bullet anticipated has since happened, on purpose, and was
 *    16rem → 18rem exactly as predicted.
 *
 * "SHRINK BUT NEVER GROW" IS THREE CLASSES AND ALL THREE ARE LOAD-BEARING:
 *  - `sm:grow-0`        — never expands into spare space. This is the whole
 *                         point of the ruling; the old order-page wrapper was
 *                         `flex-1`, i.e. grow-1, which is what made the box a
 *                         different width on each of the three pages.
 *  - `sm:shrink`        — may shrink under pressure, so a crowded toolbar wraps
 *                         gracefully instead of overflowing.
 *  - `sm:basis-[18rem]` — the size itself, expressed as the flex basis.
 *
 *    🪤 Prefer this explicit `grow-0 shrink basis-[18rem]` triple over the
 *    tempting `flex-none` + `shrink`. `flex-none` is the SHORTHAND
 *    `flex: none` (= `0 0 auto`), so it resets grow, shrink AND basis; whether
 *    a separate `shrink` longhand then wins depends on which rule comes later
 *    in the generated stylesheet, not on the order you wrote the classes in.
 *    That is a real footgun for the next editor, and it fails silently — the
 *    box simply stops shrinking.
 *
 *  - `sm:min-w-0`       — 🪤 WITHOUT THIS THE BOX WILL NOT SHRINK, however
 *                         correct the flex classes are. A flex item's default
 *                         `min-width: auto` pins it at its intrinsic content
 *                         width, so `shrink` has nothing to give. This is the
 *                         single commonest reason "it should shrink" silently
 *                         does not.
 *                         ✅ THE WORKED EXAMPLE THAT USED TO BE HERE IS GONE,
 *                         AND ITS ABSENCE IS THE POINT. It cited "a LIVE
 *                         instance at `client/src/components/PeopleManager.js
 *                         :748` (`w-64`, no `min-w-0`, inside the `flex
 *                         flex-wrap` at `:742`)" — that box adopted
 *                         `components/ui/SearchInput` on 2026-08-10, so it
 *                         takes this constant and the instance no longer
 *                         exists. Deleted rather than re-pointed at something
 *                         else: **a worked example that is not there any more
 *                         is worse than none, because the next reader goes
 *                         looking for it.** ⚠️ The citation had ALSO drifted
 *                         independently of that — by the time it was removed
 *                         `:748` was a `fetchPeople(true)` call and `:742` was
 *                         unrelated, the real site being `:848-854` inside the
 *                         toolbar at `:847`. Two separate ways for one nine-word
 *                         parenthesis to become false; the trap above needs
 *                         neither of them to stay true.
 *
 * ⚠️ Not genuinely testable under jsdom: jsdom applies no stylesheet, so flex
 * shrink behaviour cannot be asserted here. A test can pin that the CLASSES are
 * present (see `client/src/constants/inputs.width.test.js`); only a real
 * browser can prove the box behaves.
 */
export const SEARCH_BOX_WIDTH =
  'w-full sm:w-auto sm:grow-0 sm:shrink sm:basis-[18rem] sm:min-w-0';

// ---------------------------------------------------------------------------
// Search field styling
// ---------------------------------------------------------------------------

/**
 * The `<input>` itself. `pl-8` leaves room for the magnifier glyph that
 * `components/ui/SearchInput.js` renders inside the box — do not use this
 * string without that icon or the text sits in a gap.
 *
 * `py-1.5` is KEPT alongside `CONTROL_HEIGHT` and is not dead: below `md` the
 * declared height does not apply, so the padding is what gives the box its
 * shape under the 44px tap-target floor. From `md` up `md:h-9` wins outright
 * (border-box), and the padding only insets the text.
 */
export const SEARCH_INPUT_FIELD =
  // text-base below md: iOS zooms the page on focus of an input under 16px (mobile audit 2026-10-01).
  `w-full ${CONTROL_HEIGHT} pl-8 pr-3 py-1.5 text-base md:text-sm rounded-lg ` +
  'border border-gray-300 dark:border-gray-600 ' +
  'bg-white dark:bg-gray-700 text-gray-800 dark:text-gray-200 ' +
  'focus:ring-2 focus:ring-blue-500 focus:outline-none';

// ---------------------------------------------------------------------------
// Field label — ONE caption style above every form input, site-wide
// ---------------------------------------------------------------------------

/**
 * THE small caption above a form field. Owner ruling, 2026-09-28: comparing the
 * order form's TOP fields (PO Number, Requested By, Sub Department, Job/Rig/Gag,
 * Supplier, Needed by, Lead Time — `block text-sm font-medium text-gray-700
 * mb-1.5`) against that same form's LINE-ITEM fields a few hundred lines further
 * down (Item Category, Cost Code, Payment Type, Part Number, Quantity, Unit
 * Price — `block text-xs font-medium text-gray-500 mb-1`), asked to choose
 * between "match item labels exactly" and "lighter only, keep size" — he chose
 * **match exactly**. This is that value, one definition, so the two halves of
 * one form (and every other form in the app) cannot drift apart again the way
 * they already had.
 *
 * `mb-1`, not `mb-1.5` — the item style's own spacing, kept byte-for-byte
 * rather than "close enough": the ruling was to match, not to average.
 *
 * 🌓 **DARK MODE, MEASURED RATHER THAN GUESSED.** Neither source style actually
 * carried a `dark:` class in the tree before this constant — `index.css` has a
 * GLOBAL `html.dark .text-gray-500 { color: #9ca3af !important }` (and the
 * equivalent for `-600`/`-700`) that already overrides any Tailwind `dark:`
 * utility on an element also carrying the light-mode `text-gray-*` class, the
 * same mechanism documented for `bg-white`/`GAG_TABLE_HEADER_BG` elsewhere in
 * this codebase. So every existing item label was already rendering `#9ca3af`
 * in dark mode with no `dark:` class at all. `dark:text-gray-400` is included
 * here anyway — root `CLAUDE.md`'s "every Tailwind colour class MUST include a
 * `dark:` variant" rule, and `components/CLAUDE.md`'s "a shared presentational
 * component owns its own text colour (and dark variant); it never inherits it
 * from an unknown parent" — and it happens to be inert: Tailwind's own
 * `gray-400` default hex IS `#9ca3af`, so the explicit class and the global
 * override agree on every pixel. Nothing renders differently; the class is
 * simply no longer silently dependent on a page-wide stylesheet rule to be
 * correct.
 *
 * ⚠️ Not genuinely testable under jsdom for the usual reason (no stylesheet, no
 * computed colour) — `constants/inputs.fieldLabel.test.js` pins that the order
 * form's TOP fields and its ITEM fields both consume this one exported string,
 * never two literals that happen to read the same today.
 */
export const FIELD_LABEL_CLASS = 'block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1';

/**
 * THE BOX of a toolbar control that is not a text input but must read as the
 * SAME box as the search field beside it — `ui/WeekPicker` and the trigger of
 * `ui/ChecklistDropdown` (owner, 2026-10-25, Timesheets: every box — search,
 * week picker, Apply to — measured identical). Exactly `SEARCH_INPUT_FIELD`'s
 * height, radius, type size, border and fill; its padding, width and focus ring
 * are the input's own and stay out. Pinned token-for-token against
 * `SEARCH_INPUT_FIELD` by `inputs.controlBox.test.js`.
 */
export const CONTROL_BOX =
  `${CONTROL_HEIGHT} rounded-lg text-base md:text-sm ` +
  'border border-gray-300 dark:border-gray-600 ' +
  'bg-white dark:bg-gray-700 text-gray-800 dark:text-gray-200';

/**
 * THE in-cell control of a table row being edited IN PLACE — the row's own
 * cells turn into these at the column's declared width (`table-fixed` +
 * `<colgroup>`, so nothing resizes). Extracted 2026-10-25, VERBATIM, from the
 * Gag Breakdown's `GagRowEditCell.js` (where it was the file-private `CONTROL`)
 * so the four Manage Budgets tables edit in the Breakdown's control language
 * (owner: *"you should be able to use common componetns lik eon breakdown"*).
 * `w-full` is load-bearing: the control fills the cell, the cell never sizes
 * to the control. The Breakdown renders it byte-for-byte as before — its
 * `GagRowEditCell.*` / `GagGroupTable.*` tests are the proof.
 */
export const ROW_EDIT_CONTROL =
  'w-full px-1.5 py-1 text-sm rounded-md border border-blue-300 dark:border-blue-600 ' +
  'bg-white dark:bg-gray-700 text-gray-800 dark:text-gray-200 ' +
  'focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent';

/**
 * THE width of a MONEY input edited in a table row (owner, 2026-10-25: *"money
 * value inputs should be limited to 100000 value width"* — a WIDTH rule, not a
 * value cap: a larger figure is still accepted and scrolls inside the box).
 * Wide enough for "£100,000.00" and the longest in-row placeholder at the
 * control's `text-sm` — measured in the real build: "£45.45 (derived)" needs
 * 111px of text room, so 132px (8.25rem) leaves 118 for a three-digit hourly.
 * The box sits
 * RIGHT-aligned in its cell (`ml-auto`) under the right-aligned figures, and
 * `max-w-full` lets a cell dragged narrower than it shrink it rather than clip.
 */
export const MONEY_INPUT_WIDTH = 'w-[8.25rem]';
export const ROW_EDIT_MONEY_CONTROL =
  `${ROW_EDIT_CONTROL.replace('w-full ', '')} ${MONEY_INPUT_WIDTH} max-w-full block ml-auto text-right`;
// ---------------------------------------------------------------------------
// Form field — the plain bordered input / select inside a form or dialog
// ---------------------------------------------------------------------------

/**
 * THE box of a plain form input or `<select>` (not a toolbar control and not a
 * search box — those are `SEARCH_INPUT_FIELD` and the toolbar carriers):
 * full width, `CONTROL_HEIGHT` (38px desktop, the 44px tap target below `md`),
 * a 1px grey border with its dark pair. Extracted 2026-10-24 (S3b review R2)
 * out of three byte-identical copies in the timesheet screens
 * (`AwayDialog`, `UnitTimecardEditor`, `DayFieldsForm`). A caller that needs
 * more — `DayFieldsForm`'s `disabled:` pair — appends to it locally; never
 * re-type the base.
 */
export const FORM_INPUT_FIELD =
  `w-full ${CONTROL_HEIGHT} px-2.5 rounded-md border text-sm ` +
  'bg-white border-gray-300 text-gray-900 dark:bg-gray-800 dark:border-gray-600 dark:text-gray-100';
